import { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  Pencil,
  Check,
  X,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { RefineButton } from "./RefineButton";
import { cn } from "@/lib/utils";
import type { RefinementSectionType } from "@shared/refinement-types";

interface DiscoverySectionProps {
  title: string;
  icon?: React.ReactNode;
  confidence?: number;
  defaultExpanded?: boolean;
  /** External control for expansion state */
  expanded?: boolean;
  /** Callback when expansion state changes */
  onExpandedChange?: (expanded: boolean) => void;
  children: React.ReactNode;
  className?: string;
  /** Section type for refinement actions */
  sectionType?: RefinementSectionType;
  /** Discovery session ID for refinement */
  sessionId?: string;
  /** Company name for Zefix lookup */
  companyName?: string;
  /** Callback when refinement completes */
  onRefineComplete?: () => void;
  /** Number of items needing attention (conflicts, low confidence) */
  attentionCount?: number;
  /** Section status: confirmed (all good), attention (needs review), empty (no data) */
  status?: "confirmed" | "attention" | "empty";
  /** Extra content to render in the header (e.g., source badges) */
  headerExtra?: React.ReactNode;
}

export function DiscoverySection({
  title,
  icon,
  confidence,
  defaultExpanded = true,
  expanded,
  onExpandedChange,
  children,
  className,
  sectionType,
  sessionId,
  companyName,
  onRefineComplete,
  attentionCount,
  status,
  headerExtra,
}: DiscoverySectionProps) {
  // Support both controlled and uncontrolled expansion
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = expanded !== undefined ? expanded : internalExpanded;

  // Sync internal state when external control changes
  useEffect(() => {
    if (expanded !== undefined) {
      setInternalExpanded(expanded);
    }
  }, [expanded]);

  const handleToggle = () => {
    const newValue = !isExpanded;
    setInternalExpanded(newValue);
    onExpandedChange?.(newValue);
  };

  const showRefineButton = sectionType && sessionId && onRefineComplete;

  return (
    <div
      className={cn(
        "border rounded-lg bg-card transition-colors",
        status === "attention" && "border-[var(--warning)]/30",
        status === "confirmed" && "border-[var(--success)]/30",
        className
      )}
    >
      <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
        <button
          type="button"
          onClick={handleToggle}
          className="flex-1 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            {icon && (
              <span
                className={cn(
                  "text-muted-foreground",
                  status === "attention" && "text-warning",
                  status === "confirmed" && "text-success"
                )}
              >
                {icon}
              </span>
            )}
            <span className="font-semibold">{title}</span>
            {confidence !== undefined && (
              <ConfidenceBadge confidence={confidence} size="sm" />
            )}
            {attentionCount !== undefined && attentionCount > 0 && (
              <Badge
                variant="outline"
                className="text-warning border-[var(--warning)]/30 gap-1"
              >
                <AlertTriangle className="h-3 w-3" />
                {attentionCount}
              </Badge>
            )}
            {status === "confirmed" && (
              <Check className="h-4 w-4 text-success" />
            )}
            {headerExtra && <span className="ml-2">{headerExtra}</span>}
          </div>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          )}
        </button>
        {showRefineButton && (
          <RefineButton
            sectionType={sectionType}
            sessionId={sessionId}
            companyName={companyName}
            onRefineComplete={onRefineComplete}
          />
        )}
      </div>
      {isExpanded && <div className="px-4 pb-4 pt-0 border-t">{children}</div>}
    </div>
  );
}

interface EditableFieldProps {
  label: string;
  value?: string | number | null;
  path: string;
  onUpdate: (path: string, value: string) => void;
  placeholder?: string;
  type?: "text" | "number";
  suffix?: string;
  className?: string;
}

export function EditableField({
  label,
  value,
  path,
  onUpdate,
  placeholder = "Not extracted",
  type = "text",
  suffix,
  className,
}: EditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value ?? ""));

  const displayValue = value ?? "";

  const handleSave = () => {
    onUpdate(path, editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(String(value ?? ""));
    setIsEditing(false);
  };

  return (
    <div className={cn("flex items-center justify-between py-2", className)}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <Input
              type={type}
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              className="h-8 w-48"
              autoFocus
              onKeyDown={e => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") handleCancel();
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleSave}
            >
              <Check className="h-4 w-4 text-success" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleCancel}
            >
              <X className="h-4 w-4 text-destructive" />
            </Button>
          </>
        ) : (
          <>
            <span
              className={cn(
                "text-sm",
                !displayValue && "text-muted-foreground italic"
              )}
            >
              {displayValue || placeholder}
              {suffix && displayValue && ` ${suffix}`}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 opacity-0 group-hover:opacity-100 hover:opacity-100"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
